import { mapValues } from 'es-toolkit/object';
import { useMemo, useState } from 'react';
import { runInBackgroundCallback } from '../../../../../configuration/setup/backgroundActions';
import { useAppDispatch } from '../../../../../configuration/setup/hooks';
import { reportErrorToSentry } from '../../../../../configuration/setup/sentry';
import { usePutArrivalCompletionMutation, usePutDepartureCompletionMutation } from '../../../api/compoundManagementApi';
import { CompoundVehicleLoadIdConflictProblemCodec } from '../../../api/types/compoundManagement/errorCodes';
import { handleQueryError, isErrorResponse } from '../../../notifications/ErrorNotification';
import { showSuccessNotification } from '../../../notifications/SuccessNotification';
import { compoundManagementSlice, LoadPreview } from '../../../reducers/compoundManagementSlice';
import { CompoundManagementView, TransportInformation } from '../../compoundManagement.types';
import { handleDepartureCompletionError } from '../DepartureErrorHandling';
import { getStatusDateValidationMessageId } from '../sidebar/validateStatusDate';
import {
    getMeansOfTransportIdValidationMessageId,
    getMeansOfTransportTypeValidationMessageId,
} from '../sidebar/validateTransportInformation';

export type BulkStatusUpdateFormState = {
    statusAt: Date | string | undefined;
    transportInformation: Partial<TransportInformation>;
};

export type BulkStatusUpdateValidationFeedback = Record<
    'loadId' | 'statusAt' | 'meansOfTransportType' | 'meansOfTransportId',
    string | undefined
>;

export const useBulkStatusUpdateForm = (loadPreview: LoadPreview) => {
    const dispatch = useAppDispatch();

    const isArrival = loadPreview?.view === CompoundManagementView.INCOMING_VEHICLES;

    const [completeArrival, completeArrivalRequest] = usePutArrivalCompletionMutation();
    const [completeDeparture, completeDepartureRequest] = usePutDepartureCompletionMutation();
    const completeHandler = isArrival ? completeArrival : completeDeparture;

    const { formState, setFormState, resetToDefault } = useFormStateWithDefaults(loadPreview);
    const [submitted, setSubmitted] = useState(false);
    const [conflictingLoadId, setConflictingLoadId] = useState<string | undefined>(undefined);

    const hasLoadIdConflict =
        conflictingLoadId !== undefined && conflictingLoadId === formState.transportInformation.loadId;

    const successNotificationMessage = isArrival
        ? 'outboundOrderBook.compoundManagement.notification.incomingVehicle.bulkStatusUpdate.arrival.success'
        : 'outboundOrderBook.compoundManagement.notification.outgoingVehicle.bulkStatusUpdate.departure.success';

    const resetFormState = () => {
        resetToDefault();
        setSubmitted(false);
        setConflictingLoadId(undefined);
    };

    const validationErrorMessageIds: BulkStatusUpdateValidationFeedback = {
        loadId: hasLoadIdConflict
            ? 'outboundOrderBook.compoundManagement.bulkStatusUpdate.loadId.notUnique'
            : undefined,
        statusAt: getStatusDateValidationMessageId(formState.statusAt),
        meansOfTransportType: getMeansOfTransportTypeValidationMessageId(formState.transportInformation),
        meansOfTransportId: isArrival
            ? undefined
            : getMeansOfTransportIdValidationMessageId(formState.transportInformation),
    };

    const setStatusAt = (statusAt: Date | string | undefined) =>
        setFormState((previous) => ({ ...previous, statusAt }));

    const setLoadId = (loadId: string | undefined) =>
        setFormState((previous) => ({
            ...previous,
            transportInformation: { ...previous.transportInformation, loadId },
        }));

    const mergeChangedTransportInformation = (change: Partial<TransportInformation>) =>
        setFormState((previous) => ({
            ...previous,
            transportInformation: { ...previous.transportInformation, ...change },
        }));

    const submitBulkStatusCompletion = runInBackgroundCallback(async () => {
        setSubmitted(true);

        const isValid = Object.values(validationErrorMessageIds).every((messageId) => messageId === undefined);
        if (isValid) {
            try {
                await completeHandler({
                    statusAt: formState.statusAt as Date,
                    compoundVehicleIds: loadPreview?.vehicles?.map((entry) => entry.id) ?? [],
                    transportInformation: formState.transportInformation as TransportInformation,
                }).unwrap();
                resetFormState();
                dispatch(compoundManagementSlice.actions.completeBulkStatusUpdate());
                showSuccessNotification(successNotificationMessage);
            } catch (error: unknown) {
                if (isErrorResponse(error, CompoundVehicleLoadIdConflictProblemCodec)) {
                    setConflictingLoadId(formState.transportInformation.loadId);
                }
                if (isArrival) {
                    handleQueryError(error);
                } else {
                    handleDepartureCompletionError(error);
                }
            }
        }
    });

    return {
        isProcessing: completeArrivalRequest.isLoading || completeDepartureRequest.isLoading,
        formState,
        validationErrorMessageIds: mapValues(validationErrorMessageIds, (messageId) =>
            submitted ? messageId : undefined,
        ),
        setDepartureCompletedAt: setStatusAt,
        setLoadId,
        mergeChangedTransportInformation,
        submitBulkStatusCompletion,
        resetFormState,
    };
};

const useFormStateWithDefaults = (loadPreview: LoadPreview) => {
    const formStateDefaults = useMemo(() => getInitialFormState(loadPreview), [loadPreview]);
    const [internalFormState, setInternalFormState] = useState<BulkStatusUpdateFormState | undefined>();

    return {
        formState: internalFormState ?? formStateDefaults,
        setFormState: (updateFn: (previous: BulkStatusUpdateFormState) => BulkStatusUpdateFormState) => {
            setInternalFormState((previous) => updateFn(previous ?? formStateDefaults));
        },
        resetToDefault: () => setInternalFormState(undefined),
    };
};

const getInitialFormState = (loadPreview: LoadPreview): BulkStatusUpdateFormState => {
    const transportInformation =
        loadPreview?.view === CompoundManagementView.INCOMING_VEHICLES
            ? loadPreview.vehicles.map((vehicle) => vehicle.arrival.transportInformation)
            : loadPreview?.view === CompoundManagementView.OUTGOING_VEHICLES
              ? loadPreview.vehicles.map((vehicle) => vehicle.departure.transportInformation)
              : undefined;

    if (transportInformation === undefined || transportInformation.length === 0) {
        return { statusAt: undefined, transportInformation: {} };
    } else {
        const allHaveSameType = transportInformation.every(
            (info) => info.meansOfTransportType === transportInformation[0].meansOfTransportType,
        );
        if (!allHaveSameType) {
            reportErrorToSentry('Error in bulk update: Not all vehicles have the same means of transport type.');
        }

        const ids = transportInformation.map((info) => info.meansOfTransportId).filter((id) => id !== undefined);
        const allHaveSameId = ids.length > 0 ? ids.every((id) => id === ids[0]) : false;
        const trailerIds = transportInformation.map((info) => info.trailerId).filter((id) => id !== undefined);
        const allHaveSameTrailerId = trailerIds.length > 0 ? trailerIds.every((id) => id === trailerIds[0]) : false;

        return {
            statusAt: undefined,
            transportInformation: {
                meansOfTransportType: allHaveSameType ? transportInformation[0].meansOfTransportType : undefined,
                meansOfTransportId: allHaveSameId ? ids[0] : undefined,
                trailerId: allHaveSameTrailerId ? trailerIds[0] : undefined,
            },
        };
    }
};
